/**
 * The online store, as the dashboard runs it: the storefront itself, the listings that put
 * catalogue products on the site (words, options, photos, online price), and the order requests
 * customers send from it.
 *
 * Prices are not set here — an online price is an ordinary price row of type `Online`, set with
 * `setPrice` like any other, so it keeps history and passes the same lowest-price rule. The
 * listing's own tag is invalidated alongside so the page re-reads the new price.
 */

import { api } from '../api.js';
import {
  onlineListingListTag,
  onlineListingTag,
  onlineOrderListTag,
  onlineOrderTag,
  storefrontTag,
} from '../tags.js';
import type { Paged, ProductId, ShopId, VariantId } from '../../types/api.js';

// ── Types ───────────────────────────────────────────────────────────────────
//
// The API leaves empty fields out of its JSON, so a field that can be empty arrives as missing,
// not null: those are `?: T | null`, and a check for "has one" is `?? null` first, never a bare
// `!== null` (which a missing field passes).

export type StorefrontShop = { readonly shopId: ShopId; readonly shopName: string; readonly isPickupPoint: boolean };

export type Storefront = {
  readonly id: string;
  readonly code: string;
  readonly brandName: string;
  readonly byline?: string | null;
  readonly domain: string;
  readonly themeCode: string;
  readonly defaultScheme: 'dark' | 'light';
  readonly contactPhone?: string | null;
  readonly contactWhatsapp?: string | null;
  readonly heroListingId?: string | null;
  readonly heroTitle?: string | null;
  readonly heroSubtitle?: string | null;
  readonly isActive: boolean;
  readonly shops: readonly StorefrontShop[];
};

export type SaveStorefrontBody = {
  readonly code: string;
  readonly brandName: string;
  readonly byline: string | null;
  readonly domain: string;
  readonly themeCode: string;
  readonly defaultScheme: 'dark' | 'light';
  readonly contactPhone: string | null;
  readonly contactWhatsapp: string | null;
  readonly heroListingId: string | null;
  readonly heroTitle: string | null;
  readonly heroSubtitle: string | null;
  readonly isActive: boolean;
  readonly shops: readonly { shopId: ShopId; isPickupPoint: boolean }[];
};

/** available · pre-order · out-of-stock · unpriced (left off the site) · inactive · hidden */
export type OnlineAvailability = 'available' | 'pre-order' | 'out-of-stock' | 'unpriced' | 'inactive' | 'hidden';

export type PhotoUrls = { readonly w320: string; readonly w800: string; readonly w1600: string };

export type ListingPhoto = {
  readonly id: string;
  readonly color?: string | null;
  readonly sortOrder: number;
  readonly altText: string;
  readonly width: number;
  readonly height: number;
  readonly urls: PhotoUrls;
};

export type ListingOption = {
  readonly variantId: VariantId;
  readonly displayName: string;
  readonly storage?: string | null;
  readonly color?: string | null;
  readonly isVisible: boolean;
  readonly isActive: boolean;
  readonly onlinePriceKes?: number | null;
  readonly retailPriceKes?: number | null;
  readonly priceKes?: number | null;
  readonly priceSource?: 'online' | 'retail' | null;
  /** Sellable across the storefront's shops. For the office only — never shown on the site. */
  readonly stockTotal: number;
  readonly availability: OnlineAvailability;
};

export type ListingSummary = {
  readonly id: string;
  readonly slug: string;
  readonly title: string;
  readonly productName: string;
  readonly brand?: string | null;
  readonly isVisible: boolean;
  readonly isFeatured: boolean;
  readonly isPreOrder: boolean;
  readonly optionsShown: number;
  readonly optionsTotal: number;
  readonly fromPriceKes?: number | null;
  readonly toPriceKes?: number | null;
  readonly availability: OnlineAvailability;
  readonly photoUrl?: string | null;
  readonly problems: readonly string[];
};

export type ListingDetail = {
  readonly id: string;
  readonly storefrontId: string;
  readonly productId: ProductId;
  readonly productName: string;
  readonly brand?: string | null;
  readonly categoryName: string;
  readonly slug: string;
  readonly title: string;
  readonly tagline?: string | null;
  readonly description?: string | null;
  readonly isVisible: boolean;
  readonly isFeatured: boolean;
  readonly featuredOrder: number;
  readonly isPreOrder: boolean;
  readonly preOrderNote?: string | null;
  readonly publishedAt?: string | null;
  readonly options: readonly ListingOption[];
  readonly photos: readonly ListingPhoto[];
  readonly colors: readonly string[];
};

export type UpdateListingBody = {
  readonly listingId: string;
  readonly slug: string;
  readonly title: string;
  readonly tagline: string | null;
  readonly description: string | null;
  readonly isVisible: boolean;
  readonly isFeatured: boolean;
  readonly featuredOrder: number;
  readonly isPreOrder: boolean;
  readonly preOrderNote: string | null;
};

export type ProductPick = {
  readonly id: ProductId;
  readonly name: string;
  readonly brand?: string | null;
  readonly categoryName: string;
  readonly variantCount: number;
  /** Set when the product is already in the online store. */
  readonly listingId?: string | null;
};

export type OnlineOrderStatus = 'new' | 'confirmed' | 'ready' | 'collected' | 'cancelled';

export type OnlineOrderSummary = {
  readonly id: string;
  readonly orderNo: string;
  readonly status: OnlineOrderStatus;
  readonly createdAt: string;
  readonly customerName: string;
  readonly pickupShopName: string;
  readonly itemCount: number;
  readonly totalKes: number;
  readonly hasPreOrder: boolean;
};

export type OnlineOrder = {
  readonly id: string;
  readonly orderNo: string;
  readonly status: OnlineOrderStatus;
  readonly createdAt: string;
  readonly customerName: string;
  readonly customerPhone?: string | null;
  readonly customerNote?: string | null;
  readonly pickupShopId: ShopId;
  readonly pickupShopName: string;
  readonly totalKes: number;
  readonly hasPreOrder: boolean;
  readonly lines: readonly { lineNo: number; displayName: string; quantity: number; unitPriceKes: number; isPreOrder: boolean }[];
  readonly confirmedAt?: string | null;
  readonly readyAt?: string | null;
  readonly collectedAt?: string | null;
  readonly cancelledAt?: string | null;
  readonly cancelReason?: string | null;
  readonly saleId?: string | null;
  readonly receiptNo?: string | null;
  readonly handledByName?: string | null;
  readonly isAnonymised: boolean;
};

// ── Endpoints ───────────────────────────────────────────────────────────────

export const onlineStoreApi = api.injectEndpoints({
  endpoints: (build) => ({
    getStorefront: build.query<Storefront | null, void>({
      query: () => '/online/storefront',
      providesTags: [storefrontTag()],
    }),

    saveStorefront: build.mutation<Storefront, SaveStorefrontBody>({
      query: (body) => ({ url: '/online/storefront', method: 'PUT', body }),
      invalidatesTags: [storefrontTag(), onlineListingListTag()],
    }),

    searchListableProducts: build.query<ProductPick[], string>({
      query: (search) => ({ url: '/online/products', params: search ? { search } : {} }),
      providesTags: [onlineListingListTag()],
    }),

    listListings: build.query<
      Paged<ListingSummary>,
      { search?: string | undefined; visible?: boolean | undefined; page?: number | undefined; pageSize?: number | undefined }
    >({
      query: (params) => ({ url: '/online/listings', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: [onlineListingListTag()],
    }),

    createListing: build.mutation<string, { productId: ProductId }>({
      query: (body) => ({ url: '/online/listings', method: 'POST', body }),
      invalidatesTags: [onlineListingListTag()],
    }),

    getListing: build.query<ListingDetail, string>({
      query: (id) => `/online/listings/${id}`,
      providesTags: (_result, _error, id) => [onlineListingTag(id)],
    }),

    updateListing: build.mutation<void, UpdateListingBody>({
      query: ({ listingId, ...body }) => ({ url: `/online/listings/${listingId}`, method: 'PUT', body: { listingId, ...body } }),
      invalidatesTags: (_result, _error, { listingId }) => [onlineListingTag(listingId), onlineListingListTag()],
    }),

    setListingOptions: build.mutation<void, { listingId: string; options: readonly { variantId: VariantId; isVisible: boolean }[] }>({
      query: ({ listingId, options }) => ({ url: `/online/listings/${listingId}/options`, method: 'PUT', body: { listingId, options } }),
      invalidatesTags: (_result, _error, { listingId }) => [onlineListingTag(listingId), onlineListingListTag()],
    }),

    deleteListing: build.mutation<void, string>({
      query: (listingId) => ({ url: `/online/listings/${listingId}`, method: 'DELETE' }),
      invalidatesTags: [onlineListingListTag()],
    }),

    /** Multipart: the photo, what it shows, and which colour it is (empty for every colour). */
    uploadListingPhoto: build.mutation<ListingPhoto, { listingId: string; file: File; altText: string; color: string | null }>({
      query: ({ listingId, file, altText, color }) => {
        const form = new FormData();
        form.append('file', file);
        form.append('altText', altText);
        form.append('color', color ?? '');
        return { url: `/online/listings/${listingId}/photos`, method: 'POST', body: form };
      },
      invalidatesTags: (_result, _error, { listingId }) => [onlineListingTag(listingId), onlineListingListTag()],
    }),

    updateListingPhoto: build.mutation<void, { photoId: string; listingId: string; altText: string; color: string | null }>({
      query: ({ photoId, altText, color }) => ({ url: `/online/photos/${photoId}`, method: 'PUT', body: { photoId, altText, color } }),
      invalidatesTags: (_result, _error, { listingId }) => [onlineListingTag(listingId)],
    }),

    reorderListingPhotos: build.mutation<void, { listingId: string; photoIds: readonly string[] }>({
      query: ({ listingId, photoIds }) => ({ url: `/online/listings/${listingId}/photos/order`, method: 'PUT', body: { listingId, photoIds } }),
      invalidatesTags: (_result, _error, { listingId }) => [onlineListingTag(listingId), onlineListingListTag()],
    }),

    deleteListingPhoto: build.mutation<void, { photoId: string; listingId: string }>({
      query: ({ photoId }) => ({ url: `/online/photos/${photoId}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { listingId }) => [onlineListingTag(listingId), onlineListingListTag()],
    }),

    listOnlineOrders: build.query<
      Paged<OnlineOrderSummary>,
      { status?: OnlineOrderStatus | 'open' | undefined; search?: string | undefined; page?: number | undefined; pageSize?: number | undefined }
    >({
      query: (params) => ({ url: '/online/orders', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: [onlineOrderListTag()],
    }),

    getOnlineOrder: build.query<OnlineOrder, string>({
      query: (id) => `/online/orders/${id}`,
      providesTags: (_result, _error, id) => [onlineOrderTag(id)],
    }),

    stepOnlineOrder: build.mutation<void, { orderId: string; step: 'confirm' | 'ready' }>({
      query: ({ orderId, step }) => ({ url: `/online/orders/${orderId}/${step}`, method: 'POST' }),
      invalidatesTags: (_result, _error, { orderId }) => [onlineOrderTag(orderId), onlineOrderListTag()],
    }),

    collectOnlineOrder: build.mutation<void, { orderId: string; receiptNo: string }>({
      query: ({ orderId, receiptNo }) => ({ url: `/online/orders/${orderId}/collected`, method: 'POST', body: { orderId, receiptNo } }),
      invalidatesTags: (_result, _error, { orderId }) => [onlineOrderTag(orderId), onlineOrderListTag()],
    }),

    cancelOnlineOrder: build.mutation<void, { orderId: string; reason: string }>({
      query: ({ orderId, reason }) => ({ url: `/online/orders/${orderId}/cancel`, method: 'POST', body: { orderId, reason } }),
      invalidatesTags: (_result, _error, { orderId }) => [onlineOrderTag(orderId), onlineOrderListTag()],
    }),
  }),
});

export const {
  useGetStorefrontQuery,
  useSaveStorefrontMutation,
  useSearchListableProductsQuery,
  useListListingsQuery,
  useCreateListingMutation,
  useGetListingQuery,
  useUpdateListingMutation,
  useSetListingOptionsMutation,
  useDeleteListingMutation,
  useUploadListingPhotoMutation,
  useUpdateListingPhotoMutation,
  useReorderListingPhotosMutation,
  useDeleteListingPhotoMutation,
  useListOnlineOrdersQuery,
  useGetOnlineOrderQuery,
  useStepOnlineOrderMutation,
  useCollectOnlineOrderMutation,
  useCancelOnlineOrderMutation,
} = onlineStoreApi;
